import { normalizeToUTCDate } from '@mygate/common';
import type { OnboardingCohortRange, OnboardingPeriodPreset } from '@mygate/common';

const DAY_MS = 86_400_000;
const days = { DAY: 1, WEEK: 7, MONTH: 30 } satisfies Record<Exclude<OnboardingPeriodPreset, 'CUSTOM'>, number>;

/** Share rolling ranges across pages and allow for a browser clock up to one minute ahead of the API. */
export function getAnalyticsAnchor(now: Date): string {
  const intervalMs = 5 * 60_000;
  const clockSkewMarginMs = 60_000;
  return new Date(Math.floor((now.getTime() - clockSkewMarginMs) / intervalMs) * intervalMs).toISOString();
}

export function getOnboardingPresetRange(
  preset: Exclude<OnboardingPeriodPreset, 'CUSTOM'>,
  anchor: string,
): OnboardingCohortRange {
  const end = new Date(anchor);
  return { from: new Date(end.getTime() - days[preset] * DAY_MS).toISOString(), to: end.toISOString() };
}

/** Date-only inputs are UTC calendar days. Include the end day, capped at the measurement cutoff. */
export function getOnboardingCustomRange(from: string, to: string, anchor: string): OnboardingCohortRange | null {
  if (![from, to].every(value => /^\d{4}-\d{2}-\d{2}$/.test(value))) return null;
  const start = normalizeToUTCDate(from);
  const endDay = normalizeToUTCDate(to);
  if (start.toISOString().slice(0, 10) !== from || endDay.toISOString().slice(0, 10) !== to) return null;
  const cutoff = new Date(anchor);
  if (start > endDay || endDay > cutoff) return null;
  const end = new Date(Math.min(endDay.getTime() + DAY_MS, cutoff.getTime()));
  if (start >= end || end.getTime() - start.getTime() > 366 * DAY_MS) return null;
  return { from: start.toISOString(), to: end.toISOString() };
}
